import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Box,
  Chip,
  Autocomplete,
  TextField,
  ListSubheader,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import {
  HiMapPin,
  HiStar,
  HiBuildingStorefront,
  HiSparkles,
  HiEye,
  HiPencil,
} from "react-icons/hi2";
import AppPagination from "../Tools/AppPagination";
import api from "../../services/api";
import { useDispatch, useSelector } from "react-redux";
import {
  getUsedBikesInWorkshop,
  getWorkshops,
} from "../../State/Workshop/Action";
import { getStatesWithCities } from "../../State/State/Action";
import { useMyContext } from "../../store/ContextApi";

export default function WorkshopList() {
  const [page, setPage] = useState(0);
  const [selectedCity, setSelectedCity] = useState(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  let role = localStorage.getItem("role");
  const { isDealer, isAdmin } = useMyContext();
  const workshops = useSelector(
    (store) => store.workshopResponse.workshopPaginated.workshops
  );
  const totalPages = useSelector(
    (store) => store.workshopResponse.workshopPaginated.totalPages
  );
  const { statesWithCities = [], loading: statesLoading = false } = useSelector(
    (store) => store.stateResponse || {}
  );

  // Flatten cities with state information for Autocomplete
  const cityOptions = statesWithCities
    .flatMap((state) =>
      (state.cities || []).map((city) => ({
        ...city,
        stateName: state.stateName,
        stateId: state.stateId,
        label: city.cityName,
      }))
    )
    .sort((a, b) => {
      // First sort by state name
      if (a.stateName !== b.stateName) {
        return a.stateName.localeCompare(b.stateName);
      }
      // Then sort by city name within the same state
      return a.cityName.localeCompare(b.cityName);
    });

  useEffect(() => {
    dispatch(getStatesWithCities());
  }, [dispatch]);

  useEffect(() => {
    setPage(0); // Reset to first page when city changes
  }, [selectedCity]);

  useEffect(() => {
    const cityId = selectedCity?.cityId || "";
    dispatch(getWorkshops(page, cityId));
  }, [dispatch, page, selectedCity]);

  const handleViewMore = (e, workshopId) => {
    navigate(`/workshopDetail?workshopId=${workshopId}`);
  };

  const handleUpdate = (e, workshopId) => {
    navigate(`/updateWorkshop?workshopId=${workshopId}`);
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Animated Background with Gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
        {/* Decorative Blobs */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-emerald-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-teal-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-2000"></div>
        <div className="absolute bottom-0 left-1/2 w-96 h-96 bg-cyan-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-4000"></div>
      </div>

      {/* Sticky Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="sticky top-0 z-20 w-full"
      >
        <Card
          className="bg-white/90 backdrop-blur-sm shadow-md border-0 w-full"
          sx={{
            borderRadius: 0,
            background: "rgba(255, 255, 255, 0.9)",
            backdropFilter: "blur(10px)",
          }}
        >
          <CardContent className="py-3 px-4">
            <div className="flex items-center justify-center gap-2 sm:gap-3">
              <div className="p-2 bg-gradient-to-r from-emerald-500 to-teal-500 shadow-md sm:rounded-lg">
                <HiBuildingStorefront className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent whitespace-nowrap">
                Find Your Workshop
              </h1>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 py-8">
        {/* City Filter Dropdown */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
          className="mb-6 flex justify-center"
        >
          <Autocomplete
            value={selectedCity}
            onChange={(event, newValue) => {
              setSelectedCity(newValue);
            }}
            loading={statesLoading}
            sx={{
              width: { xs: "100%", sm: 400 },
              backgroundColor: "white",
              borderRadius: "16px",
              boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
              "& .MuiOutlinedInput-root": {
                borderRadius: "16px",
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#10b981",
                },
              },
            }}
            options={cityOptions}
            groupBy={(option) => option.stateName}
            getOptionLabel={(option) => option.label || option.cityName}
            renderGroup={(params) => (
              <li key={params.key}>
                <ListSubheader
                  component="div"
                  sx={{
                    backgroundColor: "#f5f5f5",
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    color: "#374151",
                    py: 1,
                    px: 2,
                    borderBottom: "1px solid #e5e7eb",
                  }}
                >
                  {params.group}
                </ListSubheader>
                <ul style={{ padding: 0, margin: 0 }}>
                  {params.children}
                </ul>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Filter by City"
                placeholder="Select city..."
              />
            )}
          />
        </motion.div>

        {/* Workshops Grid */}
        {!workshops || workshops.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-20"
          >
            <div className="w-24 h-24 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <HiBuildingStorefront className="w-12 h-12 text-emerald-600" />
            </div>
            <Typography variant="h5" className="text-gray-700 mb-2">
              No workshops available
            </Typography>
            <Typography variant="body1" className="text-gray-500">
              Check back later for new workshops
            </Typography>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <AnimatePresence>
              {workshops.map((workshop, index) => (
                <motion.div
                  key={workshop.workshopId}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                >
                  <Card
                    className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border-0 overflow-hidden h-full flex flex-col"
                    sx={{
                      borderRadius: "16px",
                      "&:hover": {
                        boxShadow: "0 25px 50px rgba(16, 185, 129, 0.15)",
                      },
                    }}
                  >
                    {/* Workshop Image */}
                    <div className="relative overflow-hidden">
                      <CardMedia
                        component="img"
                        height="240"
                        image={workshop.imageUrl}
                        alt={workshop.name}
                        className="cursor-pointer transition-transform duration-300 hover:scale-110"
                        onClick={(e) => handleViewMore(e, workshop.workshopId)}
                      />
                      <div className="absolute top-4 left-4">
                        <Chip
                          icon={<HiStar className="w-4 h-4 text-yellow-500" />}
                          label={`${workshop.rating}.0`}
                          className="bg-white/90 backdrop-blur-sm shadow-md"
                          size="small"
                        />
                      </div>
                    </div>

                    <CardContent className="flex-1 flex flex-col p-6">
                      {/* Workshop Info */}
                      <div className="mb-4">
                        <Typography
                          variant="h6"
                          className="font-bold text-gray-900 mb-2"
                          sx={{ fontSize: "1.25rem" }}
                        >
                          {workshop.name}
                        </Typography>

                        {/* Rating Stars */}
                        <div className="flex items-center gap-1 mb-2">
                          {[...Array(5)].map((_, i) => (
                            <HiStar
                              key={i}
                              className={`w-4 h-4 ${
                                i < workshop.rating
                                  ? "text-yellow-500 fill-yellow-500"
                                  : "text-gray-300"
                              }`}
                            />
                          ))}
                          <Typography
                            variant="body2"
                            className="text-gray-600 ml-2"
                          >
                            ({workshop.rating})
                          </Typography>
                        </div>

                        {/* Years Old */}
                        <Typography
                          variant="body2"
                          className="text-gray-600 mb-3"
                        >
                          {workshop.yearsOld} year(s) of experience
                        </Typography>

                        {/* Location */}
                        <Box
                          display="flex"
                          alignItems="center"
                          className="p-3 bg-gray-50 rounded-lg"
                        >
                          <HiMapPin className="w-5 h-5 text-emerald-600 mr-2 flex-shrink-0" />
                          <Typography
                            variant="body2"
                            className="text-gray-700"
                            sx={{ fontSize: "0.875rem" }}
                          >
                            {workshop.address}, {workshop.city}
                          </Typography>
                        </Box>
                      </div>

                      {/* Action Buttons */}
                      <Box display="flex" gap={1} mt="auto">
                        <Button
                          variant="contained"
                          fullWidth
                          className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white rounded-lg"
                          startIcon={<HiEye className="w-4 h-4" />}
                          onClick={(e) => handleViewMore(e, workshop.workshopId)}
                          sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            boxShadow: "0 4px 14px rgba(16, 185, 129, 0.3)",
                            "&:hover": {
                              boxShadow: "0 6px 20px rgba(16, 185, 129, 0.4)",
                            },
                          }}
                        >
                          View Details
                        </Button>
                        {isAdmin && (
                          <Button
                            variant="contained"
                            className="bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white rounded-lg"
                            startIcon={<HiPencil className="w-4 h-4" />}
                            onClick={(e) =>
                              handleUpdate(e, workshop.workshopId)
                            }
                            sx={{
                              textTransform: "none",
                              fontWeight: 600,
                              boxShadow: "0 4px 14px rgba(20, 184, 166, 0.3)",
                              "&:hover": {
                                boxShadow: "0 6px 20px rgba(20, 184, 166, 0.4)",
                              },
                            }}
                          >
                            Update
                          </Button>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}

        {/* Pagination */}
        <Box mt={6} mb={10} display="flex" justifyContent="center">
          {totalPages > 1 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              <AppPagination totalPages={totalPages} setPage={setPage} />
            </motion.div>
          )}
        </Box>
      </div>
    </div>
  );
}
